import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CheckInSystem.css';

const CheckInSystem = () => {
  const [cardNumber, setCardNumber] = useState('');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [selectedChild, setSelectedChild] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('check'); // 'check' or 'history'

  // Charger les localisations disponibles
  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations/getAllLocations');
      setLocations(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des localisations:', error);
      setMessage('❌ Impossible de charger les localisations.');
    }
  };

  // Vérifier l'abonnement
  const checkSubscription = async () => {
    if (!cardNumber) {
      setMessage('❌ Veuillez saisir un numéro de carte');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await axios.get(`/api/checkin/check-subscription/${cardNumber}`);
      
      if (response.data.exists) {
        setSubscriptionData(response.data);
        setMessage(response.data.message);
        
        // Sélectionner le premier enfant par défaut
        if (response.data.subscription.childrenNames.length > 0) {
          setSelectedChild(response.data.subscription.childrenNames[0]);
        }

        // Sélectionner la première localisation par défaut
        if (locations.length > 0) {
          setSelectedLocation(locations[0].name || locations[0]._id);
        }
      } else {
        setSubscriptionData(null);
        setMessage(response.data.message);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage('❌ ' + (error.response?.data?.message || 'Erreur lors de la vérification'));
      setSubscriptionData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Enregistrer un passage
  const handleCheckIn = async () => {
    if (!selectedChild || !selectedLocation) {
      setMessage('❌ Veuillez sélectionner un enfant et une localisation');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post('/api/checkin/check-in', {
        cardNumber,
        childName: selectedChild,
        location: selectedLocation,
        notes
      });

      setMessage(response.data.message);
      
      // Re-vérifier l'abonnement pour mettre à jour les statuts
      if (response.data.success) {
        checkSubscription();
        setNotes(''); // Réinitialiser les notes
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage('❌ ' + (error.response?.data?.message || 'Erreur lors de l\'enregistrement'));
    } finally {
      setIsLoading(false);
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Calculer le statut de la carte
  const getCardStatus = (subscription) => {
    if (subscription.isExpired) {
      return { text: 'Expirée', class: 'expired' };
    } else if (subscription.daysRemaining <= 7) {
      return { text: 'Bientôt expirée', class: 'warning' };
    } else {
      return { text: 'Valide', class: 'valid' };
    }
  };

  return (
    <div className="checkin-container">
      <div className="checkin-card">
        <h2 className="checkin-title">
          <span className="checkin-icon">🎫</span>
          Système de Contrôle d'Accès
        </h2>

        {message && (
          <div className={`checkin-message ${message.includes('❌') ? 'error' : message.includes('⚠️') ? 'warning' : 'success'}`}>
            {message}
          </div>
        )}

        {/* Recherche de carte */}
        <div className="card-search-section">
          <div className="search-input-group">
            <input
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              placeholder="Numéro de carte (ex: 001-2023)"
              className="search-input"
              onKeyPress={(e) => e.key === 'Enter' && checkSubscription()}
            />
            <button 
              onClick={checkSubscription}
              disabled={isLoading}
              className="search-btn"
            >
              {isLoading ? '🔍 Vérification...' : '🔍 Vérifier'}
            </button>
          </div>
        </div>

        {subscriptionData && (
          <div className="subscription-details">
            {/* Informations de l'abonnement */}
            <div className="subscription-header">
              <div className="client-info">
                <h3>{subscriptionData.subscription.clientName}</h3>
                <p className="card-number">{subscriptionData.subscription.cardNumber}</p>
              </div>
              <div className="subscription-status">
                <span className={`status-badge ${getCardStatus(subscriptionData.subscription).class}`}>
                  {getCardStatus(subscriptionData.subscription).text}
                </span>
                <p className="validity-date">
                  Valide jusqu'au: {formatDate(subscriptionData.subscription.validityDate)}
                </p>
                {!subscriptionData.subscription.isExpired && (
                  <p className="days-remaining">
                    ({subscriptionData.subscription.daysRemaining} jours restants)
                  </p>
                )}
              </div>
            </div>

            {/* Pack info */}
            <div className="pack-info">
              <strong>Pack:</strong> {subscriptionData.subscription.packType}
              <br />
              <strong>Enfants inclus:</strong> {subscriptionData.subscription.childrenCount}
            </div>

            {/* Navigation par onglets */}
            <div className="checkin-tabs">
              <button 
                className={`checkin-tab ${activeTab === 'check' ? 'active' : ''}`}
                onClick={() => setActiveTab('check')}
              >
                📝 Enregistrer Passage
              </button>
              <button 
                className={`checkin-tab ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                📊 Historique
              </button>
            </div>

            {/* Enregistrement de passage */}
            {activeTab === 'check' && (
              <div className="checkin-section">
                <h4>Enregistrer un Passage</h4>
                
                <div className="checkin-form">
                  <div className="form-group">
                    <label>Enfant *</label>
                    <select
                      value={selectedChild}
                      onChange={(e) => setSelectedChild(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Sélectionnez un enfant</option>
                      {subscriptionData.subscription.childrenNames.map((child, index) => (
                        <option key={index} value={child}>
                          {child}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Localisation *</label>
                    <select
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Sélectionnez une localisation</option>
                      {locations.map((location, index) => (
                        <option key={index} value={location.name || location._id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Notes (optionnel)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Notes supplémentaires..."
                      className="form-textarea"
                      rows="2"
                    />
                  </div>

                  <button 
                    onClick={handleCheckIn}
                    disabled={isLoading || !selectedChild || !selectedLocation}
                    className="checkin-btn"
                  >
                    {isLoading ? '⏳ Enregistrement...' : '✅ Enregistrer le Passage'}
                  </button>
                </div>

                {/* Statut des enfants aujourd'hui */}
                <div className="children-status">
                  <h5>Statut des passages aujourd'hui ({subscriptionData.todayStatus.date})</h5>
                  <div className="children-grid">
                    {subscriptionData.todayStatus.childrenStatus.map((child, index) => (
                      <div key={index} className={`child-status ${child.hasCheckedInToday ? 'checked-in' : 'available'}`}>
                        <div className="child-name">{child.name}</div>
                        <div className="child-status-indicator">
                          {child.hasCheckedInToday ? (
                            <span className="status-badge used">
                              ✅ Déjà passé
                              {child.lastCheckIn && ` à ${child.lastCheckIn.time}`}
                            </span>
                          ) : (
                            <span className="status-badge available">
                              🔄 Disponible
                            </span>
                          )}
                        </div>
                        {child.hasCheckedInToday && child.lastCheckIn && (
                          <div className="checkin-details">
                            <div><strong>Localisation:</strong> {child.lastCheckIn.location}</div>
                            <div><strong>Heure:</strong> {child.lastCheckIn.time}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Historique */}
            {activeTab === 'history' && (
              <div className="history-section">
                <h4>Historique des Passages</h4>
                <div className="statistics">
                  <div className="stat-item">
                    <strong>Total passages:</strong> {subscriptionData.statistics.totalCheckIns}
                  </div>
                  <div className="stat-item">
                    <strong>Cette semaine:</strong> {subscriptionData.statistics.weeklyCheckIns}
                  </div>
                  <div className="stat-item">
                    <strong>Aujourd'hui:</strong> {subscriptionData.todayStatus.totalCheckInsToday}
                  </div>
                </div>

                {subscriptionData.statistics.lastCheckIn && (
                  <div className="last-checkin">
                    <strong>Dernier passage:</strong> {subscriptionData.statistics.lastCheckIn.childName} 
                    le {formatDate(subscriptionData.statistics.lastCheckIn.date)} 
                    à {subscriptionData.statistics.lastCheckIn.location}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        {!subscriptionData && (
          <div className="instructions">
            <h4>Instructions:</h4>
            <ul>
              <li>✅ Saisissez le numéro de carte d'abonnement</li>
              <li>✅ Le système vérifie la validité de la carte</li>
              <li>✅ Chaque enfant ne peut passer qu'une fois par jour</li>
              <li>✅ Un enfant ne peut pas aller à plusieurs localisations le même jour</li>
              <li>✅ La carte doit être valide (non expirée)</li>
              <li>❌ Les passages dupliqués sont bloqués automatiquement</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckInSystem;